// Tab "KOMENTARZE" w sekcji wysyłki - pełna historia komentarzy do oferty
// (komentarze nagłówka + komentarze pozycji). Komentarze pozycji są oznaczone nazwą
// pozycji. Dolny formularz dodaje komentarz NA POZIOMIE NAGŁÓWKA (do całej oferty);
// komentarze pozycji są zarządzane z poziomu sekcji pozycji w tabeli wyżej.

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, TextField } from '@mui/material';
import { useQueryClient } from 'react-query';

import { Eye, EyeSlash } from 'react-bootstrap-icons';

import { AttachmentIcon, RemoveIcon } from 'assets/icons';

import {
  ISalesCrmOfferComment,
  useDeleteSalesCrmOfferComment,
  useGetSalesCrmOfferComments,
  usePostSalesCrmOfferComment,
  usePutSalesCrmOfferComment
} from 'api';

interface IProps {
  clientId: number;
  offerId: string;
  /** Wymuszona wersja oferty (null => aktualna). */
  versionId?: number | null;
  /** Jeśli true - pokaż tylko komentarze klienta (`authorType === 'CLIENT'`). */
  clientOnly?: boolean;
  /** Jeśli true - nie pokazuj formularza dodawania nowego komentarza. */
  readOnly?: boolean;
}

const formatDate = (raw: string | null): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const authorLabel = (
  authorType: ISalesCrmOfferComment['authorType'],
  t: (k: string) => string
): string => {
  if (authorType === 'CLIENT') return t('Klient');
  if (authorType === 'TRADER') return t('Handlowiec');
  return t('Komentarz');
};

const authorInitial = (authorType: ISalesCrmOfferComment['authorType']): string => {
  if (authorType === 'CLIENT') return 'K';
  if (authorType === 'TRADER') return 'H';
  return '?';
};

const OfferCommentsCard: FC<IProps> = ({
  clientId,
  offerId,
  versionId = null,
  clientOnly = false,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [isVisibleForClient, setIsVisibleForClient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ISalesCrmOfferComment | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [editingVisible, setEditingVisible] = useState(false);

  // Prefiks klucza - inwalidujemy listę komentarzy dla wszystkich wersji
  // (po dodaniu/edycji/usunięciu komentarza chcemy odświeżyć każdy widok).
  const commentsKeyPrefix = ['sales-crm', 'client', clientId, 'offer', offerId, 'comments'];
  const invalidate = () => queryClient.invalidateQueries(commentsKeyPrefix);

  const { data, isLoading, isError } = useGetSalesCrmOfferComments(clientId, offerId, versionId);

  const { mutate: addComment, isLoading: isAdding } = usePostSalesCrmOfferComment(clientId, offerId, {
    onSuccess: () => {
      setDraft('');
      setIsVisibleForClient(false);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e?.message || t('Nie udało się dodać komentarza.'))
  });

  const { mutate: putComment, isLoading: isSaving } = usePutSalesCrmOfferComment(clientId, offerId, {
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
    onError: (e) => setError(e?.message || t('Nie udało się zaktualizować komentarza.'))
  });

  const { mutate: deleteComment } = useDeleteSalesCrmOfferComment(clientId, offerId, {
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e?.message || t('Nie udało się usunąć komentarza.'))
  });

  // Lista pełna; opcjonalne filtrowanie do komentarzy klienta.
  const itemsAll = data?.items ?? [];
  const items = clientOnly
    ? itemsAll.filter((c) => c.authorType === 'CLIENT')
    : itemsAll;

  const handleAdd = () => {
    if (draft.trim() === '') return;
    addComment({ comment: draft.trim(), isVisibleForClient });
  };

  const handleStartEdit = (c: ISalesCrmOfferComment) => {
    setEditing(c);
    setEditingDraft(c.comment ?? '');
    setEditingVisible(c.isVisibleForClient);
  };

  const handleSaveEdit = () => {
    if (editing === null) return;
    putComment({
      commentId: editing.id,
      payload: { comment: editingDraft.trim(), isVisibleForClient: editingVisible }
    });
  };

  const handleDelete = (commentId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('Czy na pewno usunąć komentarz?'))) return;
    deleteComment(commentId);
  };

  return (
    <div>
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <CircularProgress size={20} />
        </div>
      )}

      {!isLoading && isError && (
        <div style={{ color: '#e02e4e', fontSize: 13, marginBottom: 8 }}>
          {t('Nie udało się pobrać komentarzy.')}
        </div>
      )}

      {error && (
        <div style={{ color: '#e02e4e', fontSize: 13, marginBottom: 8 }}>{error}</div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div style={{ fontSize: 13, color: '#8d9096', marginBottom: 12 }}>
          {clientOnly ? t('Brak uwag klienta.') : t('Brak komentarzy.')}
        </div>
      )}

      {!isLoading &&
        !isError &&
        items.map((c) => {
          // Komentarz nagłówka - edytowalny tu (PUT/DELETE wołają endpoint nagłówka).
          // Komentarz pozycji - read-only w tym widoku (zarządzanie w sekcji pozycji).
          const isHeaderComment = c.offerVersionPositionId === null;

          return (
            <div
              key={c.id}
              style={{
                border: '1px solid #eeeeee',
                borderLeft: c.offerVersionPositionId !== null ? '3px solid #6b54ff' : '1px solid #eeeeee',
                borderRadius: 6,
                padding: '10px 12px',
                marginBottom: 8,
                background: '#fff'
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: c.authorType === 'CLIENT' ? '#ffd180' : '#c5cae9',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#4D5158'
                  }}
                  title={authorLabel(c.authorType, t)}>
                  {authorInitial(c.authorType)}
                </div>
                <strong style={{ fontSize: 13 }}>{authorLabel(c.authorType, t)}</strong>
                {c.offerVersionPositionId !== null && (
                  <span
                    style={{
                      fontSize: 11,
                      color: '#6b54ff',
                      background: '#f3f0ff',
                      padding: '2px 8px',
                      borderRadius: 10,
                      maxWidth: 320,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={c.positionName ?? `#${c.offerVersionPositionId}`}>
                    {t('Pozycja')}: {c.positionName ?? `#${c.offerVersionPositionId}`}
                  </span>
                )}
                <span style={{ fontSize: 12, color: '#8d9096' }}>{formatDate(c.createdAt)}</span>
                <span
                  title={c.isVisibleForClient ? t('Widoczny dla klienta') : t('Ukryty przed klientem')}
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    color: c.isVisibleForClient ? '#383a3c' : '#8d9096',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                  {c.isVisibleForClient ? <Eye size={14} /> : <EyeSlash size={14} />}
                  {c.isVisibleForClient ? t('Widoczny dla klienta') : t('Ukryty przed klientem')}
                </span>
                {isHeaderComment && !readOnly && c.authorType !== 'CLIENT' && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleStartEdit(c)}
                      style={{ background: 'transparent', border: 0, cursor: 'pointer', fontSize: 12, color: '#6b54ff' }}>
                      {t('Edytuj')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(c.id)}
                      aria-label={t('Usuń komentarz')}
                      style={{ background: 'transparent', border: 0, cursor: 'pointer' }}>
                      <RemoveIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </>
                )}
              </div>

              {editing?.id === c.id ? (
                <div style={{ marginTop: 8 }}>
                  <TextField
                    multiline
                    fullWidth
                    size="small"
                    value={editingDraft}
                    onChange={(e) => setEditingDraft(e.target.value)}
                    minRows={2}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <label
                      style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      title={
                        editing?.authorType === 'CLIENT'
                          ? t('Komentarze klienta sa zawsze widoczne dla obu stron - nie mozna zmienic widocznosci.') || ''
                          : undefined
                      }>
                      <input
                        type="checkbox"
                        checked={editingVisible}
                        onChange={(e) => setEditingVisible(e.target.checked)}
                        disabled={editing?.authorType === 'CLIENT'}
                      />
                      {t('Widoczny dla klienta')}
                    </label>
                    <Button size="small" onClick={() => setEditing(null)}>
                      {t('Anuluj')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveEdit}
                      disabled={isSaving || editingDraft.trim() === ''}
                      sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
                      {isSaving ? <CircularProgress size={16} /> : t('Zapisz')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {c.comment && (
                    <div style={{ marginTop: 6, fontSize: 13, color: '#4D5158' }}>{c.comment}</div>
                  )}
                  {c.fileUrl && c.fileName && (
                    <a
                      href={c.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      download={c.fileName}
                      style={{
                        marginTop: 6,
                        display: 'inline-flex',
                        gap: 6,
                        alignItems: 'center',
                        fontSize: 12,
                        color: '#6b54ff',
                        textDecoration: 'none'
                      }}>
                      <AttachmentIcon style={{ width: 14, height: 14 }} />
                      {c.fileName}
                    </a>
                  )}
                </>
              )}
            </div>
          );
        })}

      {!readOnly && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eeeeee' }}>
          <TextField
            multiline
            fullWidth
            size="small"
            minRows={2}
            placeholder={t('Napisz komentarz do oferty')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={isVisibleForClient}
                onChange={(e) => setIsVisibleForClient(e.target.checked)}
              />
              {t('Widoczny dla klienta')}
            </label>
            <Button
              size="small"
              variant="contained"
              onClick={handleAdd}
              disabled={isAdding || draft.trim() === ''}
              sx={{ marginLeft: 'auto', backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
              {isAdding ? <CircularProgress size={16} /> : t('Dodaj komentarz')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferCommentsCard;
